import { useState } from "react";
import { Button, Modal, Select, SpinningLoader } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import CustomTable from "../../custom-table/custom-table";
import {
  DOWNLOAD_FILES_TABS,
  MONTH_OPTIONS,
  PENDING_TAB_ID,
  TABLE_PAGE_SIZE,
  YEAR_OPTIONS,
} from "./constants";
import { downloadFilesTableHeaders } from "./downloadFilesModalTableConfig";
import { useDownloadFilesModal } from "./hooks/useDownloadFilesModal";

interface DownloadFilesModalProps {
  onClose?: () => void;
}

const DownloadFilesModal = ({ onClose }: DownloadFilesModalProps) => {
  const {
    year,
    month,
    setYear,
    setMonth,
    loading,
    initialLoading,
    error,
    hasFetched,
    handleFetch,
    tableData,
    pendingTableData,
    nonPendingTableData,
    handleDownloadIconClick,
  } = useDownloadFilesModal();

  const [selectedTab, setSelectedTab] = useState<string>(
    DOWNLOAD_FILES_TABS[0].id,
  );

  const handleClose = () => {
    onClose?.();
  };

  const yearSelectOptions = YEAR_OPTIONS.map((y) => ({
    label: String(y),
    value: String(y),
  }));

  const monthSelectOptions = MONTH_OPTIONS.map((m) => ({
    label: m,
    value: m,
  }));

  const currentTableData =
    selectedTab === PENDING_TAB_ID ? pendingTableData : nonPendingTableData;

  return (
    <Modal
      dialogId={ModalId.DOWNLOAD_FILES_MODAL}
      title="Download Files"
      onClose={handleClose}
      onSave={handleClose}
      saveButtonText="Close"
      cancelText="Close"
      hideFooter
      heightPercentage={70}
    >
      {initialLoading ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3">
          <SpinningLoader />
          <span>Loading files...</span>
        </div>
      ) : (
        <div className="sftp-modal-container--table flex h-full w-full flex-col overflow-y-auto">
          <div className="mb-4 flex border-b border-primaryGray-15">
            {DOWNLOAD_FILES_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSelectedTab(tab.id)}
                className={`px-4 py-2 text-small font-medium transition-colors ${
                  tab.id === selectedTab
                    ? "border-b-2 border-tertiaryBlue-4 text-tertiaryBlue-4"
                    : "text-primaryGray-60 hover:text-primaryGray-80"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mb-4 flex w-full flex-row items-center gap-4">
            <div className="w-1/2">
              <Select
                key={`year-${year}`}
                id="download-files-year"
                label="Year"
                placeholder="Select year"
                options={yearSelectOptions}
                defaultValue={String(year)}
                onOptionChange={(option) => setYear(Number(option.value))}
              />
            </div>
            <div className="w-1/2">
              <Select
                key={`month-${month}`}
                id="download-files-month"
                label="Month"
                placeholder="Select month"
                options={monthSelectOptions}
                defaultValue={month}
                onOptionChange={(option) => setMonth(option.value)}
              />
            </div>
            <div className="mt-4 flex items-end">
              <Button
                buttonType="primary"
                size="medium"
                onClick={handleFetch}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <SpinningLoader />
                    Fetching...
                  </span>
                ) : (
                  "Fetch"
                )}
              </Button>
            </div>
          </div>

          {error && (
            <p className="mb-4 text-small text-red-600" role="alert">
              {error}
            </p>
          )}

          {!hasFetched && tableData.length === 0 && !loading ? (
            <p className="text-primaryGray-60 text-small">
              Select year and month, then click Fetch to load files for that
              month.
            </p>
          ) : hasFetched && !loading && tableData.length === 0 && !error ? (
            <p className="text-primaryGray-60 text-small">
              No files found for {year}/{month}. Select a different year or
              month and click Fetch.
            </p>
          ) : (
            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex flex-1 items-center justify-center gap-2 py-8">
                  <SpinningLoader />
                  <span>Fetching files...</span>
                </div>
              ) : (
                <CustomTable
                  tableHeaders={downloadFilesTableHeaders}
                  tableData={currentTableData}
                  tableName={TableNames.DOWNLOAD_FILES_MODAL_TABLE}
                  itemsPerPage={TABLE_PAGE_SIZE}
                  pagesPerView={4}
                  count={currentTableData.length}
                  totalCount={currentTableData.length}
                  hideSearchBar
                  onDownloadIconClick={handleDownloadIconClick}
                />
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DownloadFilesModal;
